import { useState, useEffect } from "react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { Loader2 } from "lucide-react"

interface EditFieldDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  field: string
  label: string
  currentValue: string
  onSave: (value: string) => Promise<void>
}

const COUNTRY_CODES = [
  { code: "+1", label: "USA +1" },
  { code: "+52", label: "MEX +52" },
  { code: "+593", label: "ECU +593" },
  { code: "+57", label: "COL +57" },
  { code: "+51", label: "PER +51" },
  { code: "+54", label: "ARG +54" },
  { code: "+55", label: "BRA +55" },
  { code: "+56", label: "CHL +56" },
  { code: "+34", label: "ESP +34" },
]

// Función para parsear el teléfono y extraer código de país
function parsePhoneNumber(phone: string, countryCodes: typeof COUNTRY_CODES) {
  if (!phone) return { code: "+593", number: "" }

  // Ordenar códigos por longitud descendente para matchear primero los más largos
  const sortedCodes = [...countryCodes].sort((a, b) => b.code.length - a.code.length)

  for (const country of sortedCodes) {
    if (phone.startsWith(country.code)) {
      return {
        code: country.code,
        number: phone.slice(country.code.length)
      }
    }
  }

  // Si no encuentra código, devolver el número sin el +
  return { code: "+593", number: phone.replace(/^\+/, "").replace(/\D/g, "") }
}

export function EditFieldDialog({
  open,
  onOpenChange,
  field,
  label,
  currentValue,
  onSave,
}: EditFieldDialogProps) {
  const [value, setValue] = useState(currentValue)
  const [countryCode, setCountryCode] = useState("+593")
  const [phoneNumber, setPhoneNumber] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState("")

  const isPhoneField = field === "telefono"

  // Parse phone cuando se abre el diálogo o cambia currentValue
  useEffect(() => {
    if (open) {
      if (isPhoneField && currentValue) {
        const parsed = parsePhoneNumber(currentValue, COUNTRY_CODES)
        setCountryCode(parsed.code)
        setPhoneNumber(parsed.number)
      } else {
        setValue(currentValue)
      }
      setError("")
    }
  }, [open, currentValue, isPhoneField])

  const handleSave = async () => {
    setError("")
    setIsLoading(true)

    try {
      let finalValue = value

      if (isPhoneField) {
        if (!countryCode) {
          setError("Selecciona un código de país")
          setIsLoading(false)
          return
        }
        if (!phoneNumber) {
          setError("Ingresa un número telefónico")
          setIsLoading(false)
          return
        }
        finalValue = `${countryCode}${phoneNumber}`
      }

      await onSave(finalValue)
      onOpenChange(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al guardar")
    } finally {
      setIsLoading(false)
    }
  }

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value.replace(/\D/g, "")
    if (val.startsWith("0")) {
      val = val.substring(1)
    }
    setPhoneNumber(val)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]" onOpenAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>Editar {label}</DialogTitle>
          <DialogDescription>
            Actualiza tu {label.toLowerCase()} y guarda los cambios.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {isPhoneField ? (
            <>
              <div className="grid gap-2">
                <Label htmlFor="country_code">Código de país</Label>
                <Select value={countryCode} onValueChange={setCountryCode}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar país" />
                  </SelectTrigger>
                  <SelectContent>
                    {COUNTRY_CODES.map((country) => (
                      <SelectItem key={country.code} value={country.code}>
                        {country.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="phone_number">Número telefónico</Label>
                <Input
                  id="phone_number"
                  value={phoneNumber}
                  onChange={handlePhoneChange}
                  placeholder="999999999"
                />
                <p className="text-xs text-muted-foreground">
                  Solo números, sin espacios ni guiones.
                </p>
              </div>

              {countryCode && phoneNumber && (
                <div className="rounded-md bg-muted p-3">
                  <p className="text-xs text-muted-foreground mb-1">Vista previa</p>
                  <p className="font-medium">{countryCode}{phoneNumber}</p>
                </div>
              )}
            </>
          ) : (
            <div className="grid gap-2">
              <Label htmlFor="field_value">Nuevo {label.toLowerCase()}</Label>
              <Input
                id="field_value"
                type={field === "email" ? "email" : "text"}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder={`Ingresa tu ${label.toLowerCase()}`}
              />
            </div>
          )}

          {error && (
            <p className="text-sm text-destructive">{error}</p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            disabled={isLoading || (isPhoneField ? !phoneNumber : !value)}
            className="bg-zinc-900 hover:bg-zinc-800 text-white"
          >
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
